import { Fragment, useEffect, useState } from "react";
import DateInput from "@/components/DateInput";
import api, { fmtARS, fmtDate, todayISO } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import CuentaCombobox from "@/components/CuentaCombobox";
import NumberInput from "@/components/ui/NumberInput";

export default function AsientosPage() {
  const [asientos, setAsientos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initForm());

  function initForm() {
    return {
      fecha: todayISO(), concepto: "",
      lineas: [
        { cuenta_codigo: "", cuenta_nombre: "", debe: 0, haber: 0 },
        { cuenta_codigo: "", cuenta_nombre: "", debe: 0, haber: 0 },
      ],
    };
  }

  const load = async () => {
    const { data } = await api.get("/asientos");
    setAsientos(data);
    const { data: cs } = await api.get("/cuentas");
    setCuentas(cs.filter((c) => c.imputable));
  };
  useEffect(() => { load(); }, []);

  const setLinea = (i, field, value) => {
    const lineas = [...form.lineas];
    lineas[i] = { ...lineas[i], [field]: value };
    if (field === "cuenta_codigo") {
      const cta = cuentas.find((c) => c.codigo === value);
      if (cta) lineas[i].cuenta_nombre = cta.nombre;
    }
    setForm({ ...form, lineas });
  };

  const addLinea = () => setForm({ ...form, lineas: [...form.lineas, { cuenta_codigo: "", cuenta_nombre: "", debe: 0, haber: 0 }] });
  const removeLinea = (i) => setForm({ ...form, lineas: form.lineas.filter((_, idx) => idx !== i) });

  const totalDebe = form.lineas.reduce((s, l) => s + parseFloat(l.debe || 0), 0);
  const totalHaber = form.lineas.reduce((s, l) => s + parseFloat(l.haber || 0), 0);
  const balanceOk = Math.abs(totalDebe - totalHaber) < 0.01 && totalDebe > 0;

  const save = async (e) => {
    e.preventDefault();
    if (!balanceOk) return toast.error("El asiento no balancea");
    try {
      await api.post("/asientos", {
        fecha: form.fecha, concepto: form.concepto,
        lineas: form.lineas.map((l) => ({
          ...l, debe: parseFloat(l.debe || 0), haber: parseFloat(l.haber || 0),
        })),
      });
      toast.success("Asiento registrado");
      setOpen(false);
      setForm(initForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <>
      <Topbar
        title="Asientos Contables"
        subtitle="Registro de libro diario · debe / haber."
        right={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-asiento-btn" className="rounded-sm bg-[#6B1F24] hover:bg-[#571519] h-9">
                <Plus size={16} className="mr-1" /> Nuevo asiento
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:w-[720px] sm:max-w-[720px] overflow-y-auto">
              <SheetHeader><SheetTitle className="font-heading">Nuevo Asiento</SheetTitle></SheetHeader>
              <form onSubmit={save} className="space-y-4 mt-6">
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-[12px]">Fecha</Label>
                    <DateInput className="rounded-sm mt-1 font-mono" value={form.fecha}
                      onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
                  </div>
                  <div className="col-span-2"><Label className="text-[12px]">Concepto</Label>
                    <Input required className="rounded-sm mt-1" value={form.concepto}
                      onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
                  </div>
                </div>

                <div className="border border-[#E5E5E5] rounded-sm">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                    <div className="col-span-6">Cuenta</div>
                    <div className="col-span-2 text-right">Debe</div>
                    <div className="col-span-2 text-right">Haber</div>
                    <div className="col-span-2"></div>
                  </div>
                  <div className="divide-y">
                    {form.lineas.map((l, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                        <div className="col-span-6">
                          <CuentaCombobox
                            value={l.cuenta_codigo}
                            onChange={(v) => setLinea(i, "cuenta_codigo", v)}
                            cuentas={cuentas}
                            compact
                          />
                        </div>
                        <div className="col-span-2">
                          <NumberInput step="0.01" className="rounded-sm h-8 font-mono text-right text-[12px]"
                            value={l.debe} onChange={(e) => setLinea(i, "debe", e.target.value)} />
                        </div>
                        <div className="col-span-2">
                          <NumberInput step="0.01" className="rounded-sm h-8 font-mono text-right text-[12px]"
                            value={l.haber} onChange={(e) => setLinea(i, "haber", e.target.value)} />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <button type="button" onClick={() => removeLinea(i)} className="text-red-600 hover:text-red-800">
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 border-t bg-neutral-50 text-[12px] font-mono">
                    <div className="col-span-6">
                      <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={addLinea}>
                        <Plus size={12} className="mr-1" /> Línea
                      </Button>
                    </div>
                    <div className="col-span-2 text-right">{fmtARS(totalDebe)}</div>
                    <div className="col-span-2 text-right">{fmtARS(totalHaber)}</div>
                    <div className={`col-span-2 text-right font-semibold ${balanceOk ? "num-pos" : "num-neg"}`}>
                      {balanceOk ? "Balancea" : `Δ ${fmtARS(totalDebe - totalHaber)}`}
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={!balanceOk} className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519] disabled:opacity-50">
                  Registrar asiento
                </Button>
              </form>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="p-4">
        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-5 py-2 text-left w-20">Nº</th>
                  <th className="px-5 py-2 text-left w-28">Fecha</th>
                  <th className="px-5 py-2 text-left">Concepto</th>
                  <th className="px-5 py-2 text-right w-36">Debe</th>
                  <th className="px-5 py-2 text-right w-36">Haber</th>
                </tr>
              </thead>
              <tbody>
                {asientos.map((a) => (
                  <Fragment key={a.id}>
                    <tr className="bg-white">
                      <td className="px-5 py-2 font-mono font-semibold">#{a.numero}</td>
                      <td className="px-5 py-2 font-mono">{fmtDate(a.fecha)}</td>
                      <td className="px-5 py-2">{a.concepto}</td>
                      <td className="px-5 py-2 text-right font-mono">{fmtARS(a.total_debe)}</td>
                      <td className="px-5 py-2 text-right font-mono">{fmtARS(a.total_haber)}</td>
                    </tr>
                    {a.lineas.map((l, i) => (
                      <tr key={a.id + "l" + i} className="text-[12px] text-[#666]">
                        <td></td><td></td>
                        <td className="px-5 py-1 pl-10"><span className="font-mono">{l.cuenta_codigo}</span> · {l.cuenta_nombre}</td>
                        <td className="px-5 py-1 text-right font-mono">{l.debe ? fmtARS(l.debe) : ""}</td>
                        <td className="px-5 py-1 text-right font-mono">{l.haber ? fmtARS(l.haber) : ""}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
                {asientos.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-[#999]">Sin asientos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
